
import { Component, Ref, Vue } from 'vue-property-decorator';

// Utils
import { hasPermission } from '@/utils/Permissions';
import ErrorService from '@/utils/ErrorService';

// Api
import { getFloor, updateFloor } from '@/utils/api/Floors';
import { getAllSections } from '@/utils/api/Sections';

// Components
import VAdministrationMain from '@/components/Administration/VAdministrationMain.vue';
import VAdministrationHeader from '@/components/Administration/VAdministrationHeader.vue';
import VPageTitle from '@/components/Administration/VPageTitle.vue';
import VAdministrationWidget from '@/components/Administration/VAdministrationWidget.vue';

import VAdministrationApartmentsTable from '@/components/Administration/VAdministrationApartmentsTable.vue';

import { ValidationObserver, ValidationProvider } from 'vee-validate';
import VAdministrationForm from '@/components/Administration/VAdministrationForm.vue';

import VInput from '@/components/VInput.vue';
import VButton from '@/components/VButton/VButton.vue';
import VFormField from '@/components/VFormField.vue';
import VSelect from '@/components/VSelect.vue';
import VBreadcrumbs from '@/components/VBreadcrumbs.vue';
import VPoppedAlert from '@/components/VPoppedAlert.vue';

// Enums
import PermissionsEnum from '@/ship/Enums/PermissionsEnum';

// Interfaces
type FilteredFloor = Pick<Floor, 'id' | 'name' | 'section'>;
type FilteredSection = Pick<Section, 'id' | 'name'>;
import Floor from '@/ship/Models/Floor';
import Section from '@/ship/Models/Section';
import { AxiosError } from 'axios';

@Component({
    components: {
        VAdministrationMain,
        VAdministrationHeader,
        VPageTitle,
        VAdministrationWidget,

        VAdministrationApartmentsTable,

        ValidationObserver,
        ValidationProvider,
        VAdministrationForm,

        VInput,
        VButton,
        VFormField,
        VSelect,
        VBreadcrumbs,
        VPoppedAlert,
    },
})
export default class FloorEditPage extends Vue {
    @Ref() public poppedAlert!: VPoppedAlert;

    public currentFloor: FilteredFloor | null = null;
    public sections: FilteredSection[] = [];

    public isLoading: boolean = false;
    public model: { name: string; sectionId: number | null } = {
        name: '',
        sectionId: null,
    };

    get hasCreateApartmentAccess() {
        return hasPermission(PermissionsEnum.ApartmentsCreate);
    }

    get floorId(): number {
        return +this.$route.params.id;
    }

    get currentSectionId(): number | null {
        return this.currentFloor?.section?.data.id ?? null;
    }

    get currentProjectId(): number | null {
        return this.currentFloor?.section?.data.projectId ?? null;
    }

    public async actualizeFloor() {
        const response = await getFloor<FilteredFloor>(this.floorId, {
            filter: 'id;name;project_id',
            include: 'section',
        }).catch((error) => {
            if ((error as AxiosError).response?.status === 404) this.$router.push({ name: 'error.404' });
            else ErrorService.handleApiError(error);
        });

        if (response) {
            this.currentFloor = response.data.data;

            // additionally set model values
            this.model = { name: this.currentFloor.name, sectionId: this.currentSectionId };
        }
    }

    public async actualizeSections() {
        const response = await getAllSections<FilteredSection>({
            orderBy: 'name',
            filter: 'id;name',
            search: `project_id:${this.currentProjectId}`,
            limit: 0,
        }).catch(ErrorService.handleApiError);

        if (response) this.sections = response.data.data;
    }

    public async handleSubmit() {
        this.isLoading = true;
        const response = await updateFloor(this.floorId, this.model.name, this.model.sectionId).catch(
            ErrorService.handleApiError,
        );
        if (response) this.showSuccess();
        this.isLoading = false;
    }

    public async showSuccess() {
        this.poppedAlert?.show(this.$t('dataSavedSuccessfully'));
    }

    public async created() {
        this.isLoading = true;
        await this.actualizeFloor();
        await this.actualizeSections();
        this.isLoading = false;
    }
}
